import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Wand2, Paperclip, X, Loader2, FileText, Presentation, Sparkles, Download,
  Mail, Share2, Trash2, Copy, Clock, CheckCircle2, AlertTriangle, Image as ImageIcon,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import {
  studioCreate, studioList, studioGet, studioDelete, studioOutputUrl, studioEmail,
} from "../lib/api";

const CHIPS = [
  { label: "Summarize & flag risks", cmd: "Read the attached file(s), summarize them, and flag anything important or risky I should notice." },
  { label: "Make a slide deck", cmd: "Turn this into a clear, professional slide deck." },
  { label: "Draft a document", cmd: "Draft a professional document from this." },
  { label: "Extract key numbers & dates", cmd: "Extract all the key numbers, dates, names and terms from the attached file(s)." },
];

const STATUS = {
  queued: { label: "Queued", cls: "text-amber-300 bg-amber-500/10 border-amber-400/30", Icon: Clock },
  working: { label: "Working", cls: "text-cyan-300 bg-cyan-500/10 border-cyan-400/30", Icon: Loader2 },
  done: { label: "Done", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30", Icon: CheckCircle2 },
  error: { label: "Error", cls: "text-rose-300 bg-rose-500/10 border-rose-400/30", Icon: AlertTriangle },
};

function EmailModal({ userId, job, onClose }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(job.title || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const recips = to.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!recips.length) { toast.error("Add at least one email"); return; }
    setSending(true);
    try {
      await studioEmail(job.id, userId, recips, subject, message);
      toast.success("Emailed with the PDF attached");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="studio-email-modal">
      <div className="w-full max-w-md bg-[#060b18] border border-[#00F0FF33] rounded-3xl p-5 nx-display">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#00F0FF] font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> Email this PDF</h3>
          <button onClick={onClose} data-testid="studio-email-close" className="text-cyan-200/60 hover:text-cyan-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="studio-email-to" placeholder="recipient@email.com"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="studio-email-subject" placeholder="Subject"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="studio-email-message" rows={3} placeholder="A short note (optional)"
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66] resize-none" />
        </div>
        <button onClick={send} disabled={sending} data-testid="studio-email-send"
          className="w-full mt-4 py-3 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] disabled:opacity-50 flex items-center justify-center gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send
        </button>
      </div>
    </div>
  );
}

function JobCard({ userId, job, onChanged }) {
  const [open, setOpen] = useState(job.status === "done");
  const [emailFor, setEmailFor] = useState(null);
  const st = STATUS[job.status] || STATUS.queued;
  const pdf = (job.outputs || []).find((o) => o.type === "pdf");
  const jpegs = (job.outputs || []).filter((o) => o.type === "jpeg");

  const share = async () => {
    if (!pdf) return;
    try {
      const res = await fetch(studioOutputUrl(job.id, pdf.idx, userId));
      const blob = await res.blob();
      const file = new File([blob], pdf.name, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: job.title });
      } else {
        window.open(studioOutputUrl(job.id, pdf.idx, userId), "_blank");
      }
    } catch (e) {
      window.open(studioOutputUrl(job.id, pdf.idx, userId), "_blank");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this from Nexus Studio?")) return;
    try { await studioDelete(job.id, userId); onChanged(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl overflow-hidden" data-testid={`studio-job-${job.id}`}>
      <div className="p-4 flex items-start gap-3">
        <div className="mt-0.5">
          {job.kind === "slides" ? <Presentation className="w-5 h-5 text-[#00F0FF]" />
            : job.kind === "document" ? <FileText className="w-5 h-5 text-[#00F0FF]" />
            : <Sparkles className="w-5 h-5 text-[#00F0FF]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <button onClick={() => setOpen((o) => !o)} className="text-left min-w-0" data-testid={`studio-toggle-${job.id}`}>
              <p className="text-cyan-50 font-semibold truncate">{job.title}</p>
              {job.command && <p className="text-cyan-200/45 text-xs truncate mt-0.5">{job.command}</p>}
            </button>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2 py-1 rounded-full text-[10px] font-bold border flex items-center gap-1 ${st.cls}`} data-testid={`studio-status-${job.id}`}>
                <st.Icon className={`w-3 h-3 ${job.status === "working" ? "animate-spin" : ""}`} /> {st.label}
              </span>
              <button onClick={remove} data-testid={`studio-delete-${job.id}`} className="p-1 rounded-lg text-rose-300/50 hover:text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {(job.files || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {job.files.map((f, i) => (
                <span key={i} className="text-[10px] text-cyan-200/50 bg-[#00F0FF08] border border-[#00F0FF18] rounded px-1.5 py-0.5 flex items-center gap-1">
                  <Paperclip className="w-2.5 h-2.5" /> {f.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-[#00F0FF14] pt-3" data-testid={`studio-detail-${job.id}`}>
          {job.status === "error" && (
            <p className="text-rose-300 text-sm">Nexus hit a problem: {job.error || "unknown error"}</p>
          )}
          {(job.status === "queued" || job.status === "working") && (
            <p className="text-cyan-200/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Nexus is working on this — you can close the app and come back.</p>
          )}
          {job.status === "done" && (
            <>
              {jpegs.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                  {jpegs.map((o) => (
                    <a key={o.idx} href={studioOutputUrl(job.id, o.idx, userId)} target="_blank" rel="noreferrer"
                      className="shrink-0 border border-[#00F0FF22] rounded-lg overflow-hidden hover:border-[#00F0FF66]" data-testid={`studio-thumb-${job.id}-${o.idx}`}>
                      <img src={studioOutputUrl(job.id, o.idx, userId)} alt={o.name} className="h-28 w-auto object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {job.result && (
                <div className="nx-md text-cyan-100/90 text-sm max-h-72 overflow-y-auto mb-3 pr-1">
                  <ReactMarkdown>{job.result}</ReactMarkdown>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {pdf && (
                  <a href={studioOutputUrl(job.id, pdf.idx, userId, true)}
                    className="px-3 py-2 rounded-full text-xs font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] flex items-center gap-1.5" data-testid={`studio-download-pdf-${job.id}`}>
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                )}
                {jpegs.length > 0 && (
                  <a href={studioOutputUrl(job.id, jpegs[0].idx, userId, true)}
                    className="px-3 py-2 rounded-full text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5" data-testid={`studio-download-jpeg-${job.id}`}>
                    <ImageIcon className="w-3.5 h-3.5" /> JPEGs
                  </a>
                )}
                <button onClick={share} data-testid={`studio-share-${job.id}`}
                  className="px-3 py-2 rounded-full text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5" /> Forward
                </button>
                <button onClick={() => setEmailFor(job)} data-testid={`studio-email-${job.id}`}
                  className="px-3 py-2 rounded-full text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email
                </button>
                {job.result && (
                  <button onClick={() => { navigator.clipboard.writeText(job.result); toast.success("Copied"); }} data-testid={`studio-copy-${job.id}`}
                    className="px-3 py-2 rounded-full text-xs font-bold text-cyan-200/60 border border-[#00F0FF22] hover:bg-[#00F0FF08] flex items-center gap-1.5">
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {emailFor && <EmailModal userId={userId} job={emailFor} onClose={() => setEmailFor(null)} />}
    </div>
  );
}

export default function NexusStudio({ session }) {
  const userId = session.user_id;
  const [command, setCommand] = useState("");
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await studioList(userId); setJobs(r.jobs || []); } catch { /* noop */ }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Poll while any job is active
  useEffect(() => {
    const active = jobs.some((j) => j.status === "queued" || j.status === "working");
    if (!active) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length) setFiles((p) => [...p, ...chosen]);
    e.target.value = "";
  };

  const submit = async () => {
    if (!command.trim() && files.length === 0) { toast.error("Type a command or attach a file"); return; }
    setSubmitting(true);
    try {
      await studioCreate(userId, command, files);
      setCommand(""); setFiles([]);
      toast.success("Sent to Nexus — he's on it");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] nx-display" data-testid="nexus-studio-panel">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-5">
          <h2 className="text-[#00F0FF] font-bold text-xl flex items-center gap-2"><Wand2 className="w-6 h-6" /> Nexus Studio</h2>
          <p className="text-cyan-200/50 text-sm mt-1">Drop files, tell Nexus what to do. He works in the background and hands you a PDF + images you can forward or email.</p>
        </div>

        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-3xl p-4 mb-6">
          <textarea value={command} onChange={(e) => setCommand(e.target.value)} data-testid="studio-command-input" rows={3}
            placeholder='Tell Nexus what you need — e.g. "Read this lease and make a 5-slide summary" or "Draft a firm payment-reminder letter as a PDF"'
            className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-2xl px-3 py-3 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66] resize-none" />

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="studio-file-chips">
              {files.map((f, i) => (
                <span key={i} className="text-xs text-cyan-100 bg-[#00F0FF11] border border-[#00F0FF33] rounded-full px-3 py-1 flex items-center gap-2">
                  <Paperclip className="w-3 h-3" /> <span className="max-w-[160px] truncate">{f.name}</span>
                  <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-cyan-200/50 hover:text-rose-300"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mt-3">
            {CHIPS.map((c) => (
              <button key={c.label} onClick={() => setCommand(c.cmd)} data-testid={`studio-chip-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-[11px] text-cyan-200/70 border border-[#00F0FF22] rounded-full px-2.5 py-1 hover:bg-[#00F0FF11] hover:text-cyan-100">
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={pick} data-testid="studio-file-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="studio-attach-btn"
              className="px-4 py-2.5 rounded-full text-sm font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1.5">
              <Paperclip className="w-4 h-4" /> Attach files
            </button>
            <button onClick={submit} disabled={submitting} data-testid="studio-send-btn"
              className="px-5 py-2.5 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] disabled:opacity-50 flex items-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Send to Nexus
            </button>
          </div>
        </div>

        {jobs.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[#00F0FF22] rounded-3xl" data-testid="studio-empty">
            <Wand2 className="w-9 h-9 text-[#00F0FF66] mx-auto mb-3" />
            <p className="text-cyan-100 font-semibold">Nothing yet</p>
            <p className="text-cyan-200/50 text-sm mt-1">Give Nexus your first task above.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="studio-jobs">
            {jobs.map((j) => <JobCard key={j.id} userId={userId} job={j} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}
